import {Component, ElementRef, forwardRef, Input, ViewChild, AfterViewInit} from '@angular/core';
import {NG_VALUE_ACCESSOR} from '@angular/forms';
import {Modal, ModalController} from 'ionic-angular';
import {QuillEditorComponent} from 'ngx-quill';
import {AbstractInput} from '../../../../../shared/components/inputs/abstract';
import {QuillEditorContentChangedInterface} from '../../../interfaces/quill-editor-content-changed.interface';
import {ShortcodeInterface} from '../../../interfaces/shortcode.interface';
import {HrBlot} from '../body/quill/blots/hr';
import {Image} from '../body/quill/formats/image/image.format';
import {Strike} from '../body/quill/formats/strike/strike.format';
import {IOSSafariQuillEditorModule} from '../body/quill/modules/ios-editor.quill.module';
import {CaptionInputModalComponent} from './caption-input-modal';
import Quill from 'quill';

/**
 * Class for the CaptionInput
 * @class CaptionInput
 * @description Component to hold the [Quill Editor]{@link https://quilljs.com/} and facilitate the full screen rte for the image card.
 * Utilizes the [ngx-quill]{@link https://github.com/KillerCodeMonkey/ngx-quill} package
 *
 * @example
 *
 * ```html
 *
 * <advtech-caption-input
 *  #caption
 *  formControlName="caption"
 *  [showToolbar]="false"
 *  [modalHeader]="form.getEntity().getTitle().getValue()"
 * ></advtech-caption-input>
 * ```
 */
@Component({
  providers: [
    {
      multi: true,
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CaptionInput),
    },
  ],
  selector: 'advtech-caption-input',
  templateUrl: './caption.input.html',
})
export class CaptionInput extends AbstractInput implements AfterViewInit {

  /**
   * An instance of the QuillEditorComponent
   * @type {QuillEditorComponent}
   */
  @ViewChild(QuillEditorComponent)
  quillEditor: QuillEditorComponent;

  /**
   * Input to set the title display on the modal
   * @default
   * @type {boolean}
   */
  @Input()
  modalHeader: string;

  /**
   * Input to do modal open on clicking the body
   * @default
   * @type {boolean}
   */
  @Input()
  doModalOpen = true;

  /**
   * Input to show the toolbar or not
   * @default
   * @type {boolean}
   */
  @Input()
  showToolbar = true;

  /**
   * Input to show the body label or not
   * @default
   * @type {boolean}
   */
  @Input()
  showLabel = true;

  /**
   * Input to set the placeholder on the quill editor
   * @default
   * @type {boolean}
   */
  @Input()
  placeholder = 'Click to edit';

  /**
   * Input to make the editor readonly
   * @default
   * @type {boolean}
   */
  @Input()
  readOnly = true;

  /**
   * The quill editor's elementRef
   * @type {ElementRef}
   */
  @ViewChild('quill', {read: ElementRef})
  quillEditorElement: ElementRef;

  bodyModal: Modal;

  /** @inheritdoc */
  constructor(public modalCtrl: ModalController) {
    super();
  }

  /** @inheritdoc */
  ngAfterViewInit() {
    if (this.data) {
      this.setValue(this.data.value || this.data);
    }
    if (this.doModalOpen) {
      this.quillEditor.onContentChanged.subscribe((res: QuillEditorContentChangedInterface) => {
        this.onChange(res.html);
        this.dispatchChangeEvent();
      });
    }
  }

  /**
   * Dispatches the change event for the quill editor
   * @return {void}
   */
  dispatchChangeEvent(): void {
    const evt = document.createEvent('HTMLEvents');
    evt.initEvent('change', false, true);
    this.quillEditor.quillEditor.root.dispatchEvent(evt);
  }

  /**
   * Sets the value in the quill editor
   * @param {string} value
   * @return {BodyInput}
   */
  setValue(value: string): this {
    if (value) {
      super.setValue(value);
      this.quillEditor.quillEditor.pasteHTML(value);
      this.dispatchChangeEvent();
    }
    return this;
  }

  /**
   * Gets the value of the Quill editor
   * @returns {string}
   */
  getValue(): string {
    return this.quillEditor.editorElem.children[0].innerHTML;
  }

  getModelValue(container) {
    let html = container.innerHTML;
    const shortcodes = container.querySelectorAll('[data-shortcode]');
    Array.from(shortcodes).forEach((node: Element) => {
      const shortcodeBlot: ShortcodeInterface = Quill.find(node);
      const newNode = shortcodeBlot.getModelTag();
      html = html.replace(node.outerHTML, newNode.outerHTML);
    });

    return html;
  }

  /** @inheritdoc */
  hClick(e: Event) {
    if (this.doModalOpen) {
      this.bodyModal = this.modalCtrl.create(CaptionInputModalComponent, {
        title: this.modalHeader,
        value: this.getValue(),
      });
      this.bodyModal.onDidDismiss((val) => this.setValue(val.value));
      this.bodyModal.present();
    }
  }

  /**
   * get the quill config
   * @return {object}
   */
  getQuillModuleConfig(): {} {
    this.registerQuillExtras({
      blots: [
        HrBlot,
      ],
      formats: [
        Strike,
        Image,
      ],
      modules: [
        {
          name: 'ios_editor',
          class: IOSSafariQuillEditorModule,
        },
      ],
    });
    return {
      toolbar: this.getToolbarConfig(),
      ios_editor: this.showToolbar,
      clipboard: {
        matchVisual: false,
      },
    };
  }

  /**
   * Registers quill extras like modules and blots
   * @param {{blots: [], formats: [], modules: Array<{name, class}>}} obj Blots and modules
   * @returns {void}
   */
  registerQuillExtras({blots, formats, modules}): void {
    this.registerBlots(blots);
    this.registerModules(modules);
    this.registerFormats(formats);
    Quill.debug('');
  }

  /**
   * Register custom formats
   * @param {any[]} formats array of format classes
   * @returns {void}
   */
  registerFormats(formats: any[]): void {
    for (const format of formats) {
      Quill.register(format, true);
    }
  }

  /**
   * Register custom blots
   * @param {any[]} blots array of blot classes
   * @returns {void}
   */
  registerBlots(blots: any[]): void {
    for (const blot of blots) {
      Quill.register(blot);
    }
  }

  /**
   * Registers custom modules
   * @param {Array<{name: string, object: {}}>} modules li
   * @returns {void}
   */
  registerModules(modules) {
    for (const module of modules) {
      Quill.register(`modules/${module.name}`, module.class);
    }
  }

  /**
   * Sets the disabled state
   * @param {boolean} isDisabled should disable
   * @returns {boolean}
   */
  setDisabledState(isDisabled: boolean): boolean {
    return true;
  }

  /** @inheritdoc */
  protected getToolbarConfig(): any {
    if (!this.showToolbar) {
      return false;
    }
    return [
      [
        'bold',
        'italic',
        'strike',
        'link',
        'clean',
      ],
    ];
  }
}



// WEBPACK FOOTER //
// ./src/app/content/shared/components/inputs/caption/caption.input.ts